import { BOARD_SIZE, type GameState, type Move } from "../game";
import type { SearchBudget } from "./difficulty";
import { HEURISTIC_SCORE_LIMIT, TERMINAL_SCORE, type TerminalAssessment } from "./evaluation";
import {
  applyFastMove,
  createFastPosition,
  encodeFastMove,
  evaluateFastMaterial,
  fastPlayer,
  fastPositionKey,
  fastRepetitionKey,
  generateFastMoves,
  type FastPlayer,
  type FastPosition,
} from "./fastPosition";
import { RepetitionTracker } from "./repetitionTracker";
import { BoundedTranspositionTable, type TranspositionBound } from "./transpositionTable";

const NEGATIVE_INFINITY = -TERMINAL_SCORE * 2;
const POSITIVE_INFINITY = TERMINAL_SCORE * 2;
const EXTREME_HEURISTIC_LIMIT = HEURISTIC_SCORE_LIMIT - 10_000;

export type FastScoredMove = Readonly<{
  move: Move;
  score: number;
  provenTerminal: TerminalAssessment;
}>;

export type FastSearchResult = Readonly<{
  rankedMoves: readonly FastScoredMove[];
  nodes: number;
  completedDepth: number;
  exhaustedBudget: boolean;
}>;

type Context = {
  readonly perspective: FastPlayer;
  readonly nodeLimit: number;
  readonly table: BoundedTranspositionTable;
  readonly repetitions: RepetitionTracker;
  nodes: number;
};

type Value = Readonly<{ score: number; terminal: TerminalAssessment }>;

class NodeBudgetExhausted extends Error {}

function evaluate(
  position: FastPosition,
  perspective: FastPlayer,
  legalMoves?: readonly number[],
): Value {
  if (position.winner !== -1) {
    const won = position.winner === perspective;
    return { score: won ? TERMINAL_SCORE : -TERMINAL_SCORE, terminal: won ? "win" : "loss" };
  }

  const moves = legalMoves ?? generateFastMoves(position);
  const activeSign = position.active === perspective ? 1 : -1;
  let score = evaluateFastMaterial(position, perspective);
  score += activeSign * moves.length * 8;
  score += activeSign * moves.filter((move) => ((move >> 11) & 1) === 1).length * 100_000;
  return {
    score: Math.max(-EXTREME_HEURISTIC_LIMIT, Math.min(EXTREME_HEURISTIC_LIMIT, score)),
    terminal: null,
  };
}

function terminalDistance(value: Value): Value {
  // Bound cutoffs intentionally clear terminal metadata, so recognize the reserved
  // score band as well. Heuristics are clamped below this band.
  if (value.score > EXTREME_HEURISTIC_LIMIT) {
    return { score: value.score - 1, terminal: value.terminal };
  }
  if (value.score < -EXTREME_HEURISTIC_LIMIT) {
    return { score: value.score + 1, terminal: value.terminal };
  }
  return value;
}

function moveOrderingScore(position: FastPosition, move: number): number {
  if (((move >> 11) & 1) === 1) return TERMINAL_SCORE;
  const from = move & 31;
  const fromRow = Math.floor(from / BOARD_SIZE);
  const toRow = ((move >> 5) & 7) - 1;
  const toColumn = ((move >> 8) & 7) - 1;
  const to = toRow * BOARD_SIZE + toColumn;
  const destination = position.cells[to] ?? 0;
  const destinationHeight = destination === 0 ? 0 : 31 - Math.clz32(destination);
  const progressDelta = position.active === 0 ? fromRow - toRow : toRow - fromRow;
  const centrality = 4 - Math.abs(2 - toRow) - Math.abs(2 - toColumn);
  return destinationHeight * 40 + progressDelta * 12 + centrality;
}

function orderedMoves(position: FastPosition, moves: number[]): number[] {
  return moves.sort(
    (left, right) =>
      moveOrderingScore(position, right) - moveOrderingScore(position, left) || left - right,
  );
}

function consumeNode(context: Context): void {
  if (context.nodes >= context.nodeLimit) throw new NodeBudgetExhausted();
  context.nodes += 1;
}

function searchChild(
  position: FastPosition,
  depth: number,
  alpha: number,
  beta: number,
  context: Context,
): Value {
  if (position.winner !== -1) return alphaBeta(position, depth, alpha, beta, context);
  const key = fastRepetitionKey(position);
  if (context.repetitions.enter(key)) return { score: 0, terminal: "draw" };
  try {
    return alphaBeta(position, depth, alpha, beta, context);
  } finally {
    context.repetitions.leave(key);
  }
}

function alphaBeta(
  position: FastPosition,
  depth: number,
  alphaInput: number,
  betaInput: number,
  context: Context,
): Value {
  consumeNode(context);
  if (position.winner !== -1) return evaluate(position, context.perspective);

  const moves = generateFastMoves(position);
  if (depth === 0) return evaluate(position, context.perspective, moves);

  const key = `${fastPositionKey(position)}|${context.repetitions.signature(depth)}`;
  const cached = context.table.get(key);
  let alpha = alphaInput;
  let beta = betaInput;
  if (cached !== undefined && cached.depth >= depth) {
    if (cached.bound === "exact") return { score: cached.score, terminal: cached.terminal };
    if (cached.bound === "lower") alpha = Math.max(alpha, cached.score);
    else beta = Math.min(beta, cached.score);
    if (alpha >= beta) return { score: cached.score, terminal: null };
  }

  const originalAlpha = alpha;
  const originalBeta = beta;
  const maximizing = position.active === context.perspective;
  let best = maximizing ? NEGATIVE_INFINITY : POSITIVE_INFINITY;
  let bestTerminal: TerminalAssessment = null;
  for (const move of orderedMoves(position, moves)) {
    const value = terminalDistance(
      searchChild(applyFastMove(position, move), depth - 1, alpha, beta, context),
    );
    if (maximizing) {
      if (value.score > best) {
        best = value.score;
        bestTerminal = value.terminal;
      }
      alpha = Math.max(alpha, best);
    } else {
      if (value.score < best) {
        best = value.score;
        bestTerminal = value.terminal;
      }
      beta = Math.min(beta, best);
    }
    if (alpha >= beta) break;
  }

  let bound: TranspositionBound = "exact";
  if (best <= originalAlpha) bound = "upper";
  else if (best >= originalBeta) bound = "lower";
  const terminal = bound === "exact" ? bestTerminal : null;
  context.table.set(key, { depth, score: best, bound, terminal });
  return { score: best, terminal };
}

export function searchFastExtreme(
  state: GameState,
  rootMoves: readonly Move[],
  budget: SearchBudget,
  maxCompletedDepth = budget.maxDepth,
  positionKeys?: readonly string[],
  table: BoundedTranspositionTable = new BoundedTranspositionTable(budget.tableCapacity, true),
): FastSearchResult {
  const position = createFastPosition(state);
  const encoded = rootMoves.map(encodeFastMove);
  const context: Context = {
    perspective: fastPlayer(state.activePlayer),
    nodeLimit: budget.nodeLimit,
    table,
    repetitions: new RepetitionTracker(positionKeys ?? [fastRepetitionKey(position)]),
    nodes: 0,
  };
  let completedDepth = 0;
  let exhaustedBudget = false;
  let rankedMoves: readonly FastScoredMove[] = rootMoves.map((move) => ({
    move,
    score: 0,
    provenTerminal: null,
  }));

  for (let depth = 1; depth <= maxCompletedDepth; depth += 1) {
    try {
      const scored: FastScoredMove[] = [];
      let rootAlpha = NEGATIVE_INFINITY;
      for (let index = 0; index < encoded.length; index += 1) {
        const move = encoded[index];
        const canonical = rootMoves[index];
        if (move === undefined || canonical === undefined) continue;
        const value = terminalDistance(
          searchChild(
            applyFastMove(position, move),
            depth - 1,
            rootAlpha,
            POSITIVE_INFINITY,
            context,
          ),
        );
        const rawScore = value.score;
        const score = rawScore <= rootAlpha ? rootAlpha - 1 : rawScore;
        rootAlpha = Math.max(rootAlpha, rawScore);
        scored.push({
          move: canonical,
          score,
          provenTerminal: score === rawScore ? value.terminal : null,
        });
      }
      scored.sort((left, right) => right.score - left.score);
      rankedMoves = scored;
      completedDepth = depth;
    } catch (error) {
      if (!(error instanceof NodeBudgetExhausted)) throw error;
      exhaustedBudget = true;
      break;
    }
  }

  return { rankedMoves, nodes: context.nodes, completedDepth, exhaustedBudget };
}
